/**
 * TriggerEditorDialog
 *
 * Shared editor for BOTH marketing and transactional email triggers.
 *
 * Replaces the "Trigger Editor — Coming Soon" placeholder on the marketing page
 * and the HC-3 read-only lock on the transactional page. The backend PUT route
 * (/api/admin/email-triggers/:id) has existed all along; only the UI was missing.
 *
 * HC-3 note: transactional triggers were previously frozen outright. That freeze
 * was lifted deliberately on 2026-08-18 and replaced with a narrower control —
 * a dedicated permission enforced SERVER-SIDE plus a full before/after audit-log
 * entry on every change. `readOnlyReason` here is a courtesy affordance only; the
 * server is the authority and will reject an unpermitted edit with HC3_PERMISSION_REQUIRED.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Switch,
  FormControlLabel,
  Typography,
  CircularProgress,
} from "@mui/material";
import apiService from "../../../services/api";

export interface EditableTrigger {
  id: string;
  trigger_key?: string;
  trigger_name?: string;
  description?: string | null;
  event_type?: string;
  template_id?: string | null;
  delay_minutes?: number | null;
  priority?: string | number;
  target_audience?: string | null;
  cooldown_hours?: number | null;
  is_enabled?: boolean;
  status?: string;
  kind?: "transactional" | "nurture";
}

interface Props {
  open: boolean;
  trigger: EditableTrigger | null;
  /** 'edit' (default) PUTs to /:id; 'create' POSTs a new trigger. */
  mode?: "edit" | "create";
  /** When set, the form is disabled and this explains why. */
  readOnlyReason?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const PRIORITIES = ["low", "medium", "high", "urgent"];
const AUDIENCES = ["all_users", "premium_only", "free_tier", "trial_users", "custom"];
const STATUSES = ["active", "inactive", "paused"];

const TriggerEditorDialog: React.FC<Props> = ({
  open,
  trigger,
  mode = "edit",
  readOnlyReason = null,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = useState<EditableTrigger>({ id: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (trigger) {
      setForm({ ...trigger });
      setError(null);
    }
  }, [trigger]);

  const disabled = Boolean(readOnlyReason) || saving;
  const set = (k: keyof EditableTrigger) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (mode === "edit" && !form.id) return;
    setSaving(true);
    setError(null);
    try {
      // Send only the fields the backend allow-lists, and only when present.
      const payload: Record<string, unknown> = {};
      if (form.trigger_name !== undefined) payload.trigger_name = form.trigger_name;
      if (form.description !== undefined) payload.description = form.description ?? "";
      if (form.event_type !== undefined) payload.event_type = form.event_type;
      if (form.delay_minutes !== undefined && form.delay_minutes !== null)
        payload.delay_minutes = Number(form.delay_minutes);
      if (form.priority !== undefined) payload.priority = form.priority;
      if (form.target_audience) payload.target_audience = form.target_audience;
      if (form.cooldown_hours !== undefined && form.cooldown_hours !== null)
        payload.cooldown_hours = Number(form.cooldown_hours);
      if (form.is_enabled !== undefined) payload.is_enabled = form.is_enabled;
      if (form.status !== undefined) payload.status = form.status;

      if (mode === "create") {
        // POST /api/admin/email-triggers requires trigger_key, trigger_name,
        // event_type, template_id. Derive a stable key from the name.
        const key =
          form.trigger_key ||
          (form.trigger_name || "trigger")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "_")
            .replace(/^_+|_+$/g, "");
        await apiService.post("/api/admin/email-triggers", {
          ...payload,
          trigger_key: key,
          template_id: form.template_id || null,
        });
      } else {
        await apiService.put(`/api/admin/email-triggers/${form.id}`, payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      // The server rejects an unpermitted transactional edit with a specific code.
      const code = err?.response?.data?.code;
      setError(
        code === "HC3_PERMISSION_REQUIRED"
          ? "You do not have permission to edit transactional triggers. This requires the transactional_triggers permission."
          : err?.response?.data?.message || err?.message || "Failed to save trigger",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === "create" ? "New Trigger" : "Edit Trigger"}
        {form.trigger_key ? (
          <Typography variant="body2" color="text.secondary">
            {form.trigger_key}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {readOnlyReason && <Alert severity="info">{readOnlyReason}</Alert>}
          {form.kind === "transactional" && !readOnlyReason && (
            <Alert severity="warning">
              This is a <strong>transactional</strong> trigger. Changes take effect
              immediately and every edit is recorded in the admin audit log with a
              full before/after record.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Trigger name"
            value={form.trigger_name ?? ""}
            onChange={set("trigger_name")}
            disabled={disabled}
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description ?? ""}
            onChange={set("description")}
            disabled={disabled}
            fullWidth
            multiline
            minRows={2}
          />
          {mode === "create" && (
            <TextField
              label="Template ID"
              value={form.template_id ?? ""}
              onChange={set("template_id")}
              disabled={disabled}
              fullWidth
              helperText="UUID of the email template this trigger sends. Required."
            />
          )}
          <TextField
            label="Event type"
            value={form.event_type ?? ""}
            onChange={set("event_type")}
            disabled={disabled}
            fullWidth
            helperText="The event name that fires this trigger."
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Delay (minutes)"
              type="number"
              value={form.delay_minutes ?? 0}
              onChange={set("delay_minutes")}
              disabled={disabled}
              fullWidth
            />
            <TextField
              label="Cooldown (hours)"
              type="number"
              value={form.cooldown_hours ?? 0}
              onChange={set("cooldown_hours")}
              disabled={disabled}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Priority"
              value={String(form.priority ?? "medium")}
              onChange={set("priority")}
              disabled={disabled}
              fullWidth
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={form.status ?? "active"}
              onChange={set("status")}
              disabled={disabled}
              fullWidth
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            select
            label="Target audience"
            value={form.target_audience ?? "all_users"}
            onChange={set("target_audience")}
            disabled={disabled}
            fullWidth
          >
            {AUDIENCES.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(form.is_enabled)}
                onChange={(e) => setForm((f) => ({ ...f, is_enabled: e.target.checked }))}
                disabled={disabled}
              />
            }
            label="Enabled"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={disabled}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerEditorDialog;
